import config from "@/config";
import { captureWebHandoffLink } from "./webOnboardingSession";
import { MetaAttribution, User } from "@um/contracts";
import { auth } from "@um/ui";
import { getDocument } from "@um/ui";
import { logEvent, logException } from "@um/ui";
import { Capacitor } from "@capacitor/core";
import { AFConstants, AppsFlyer } from "appsflyer-capacitor-plugin";

import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";
import { setDoc, Timestamp } from "firebase/firestore";

// To enable the facebook realtime event testing on IOS you need to set this to true.
// This will prompt for additional permissions to use the advertiser ID tracking so this should be only used for testing event integration.
// NOTE: You also need to sign into the facebook app on the same device for this to work.
// DO NOT SET THIS TO TRUE IN PRODUCTION.
// For this to work on iOS you will also need to add the following to your Info.plist:
//   <key>NSUserTrackingUsageDescription</key>
//   <string>For testing only. Do not ship!</string>
const ENABLE_IDFA_TRACKING = false;

// The Meta (Facebook) Pixel base code (loader + `init` + initial PageView) is
// installed inline in index.html, guarded to web only — native builds report
// through AppsFlyer instead. Pixel id 1358150222094807 lives there. This module
// only fires *conversion* events against the already-initialized `window.fbq`.

// ---------------------------------------------------------------------------
// Server-side tagging (sGTM) — first-party transport + Meta Conversions API.
// ---------------------------------------------------------------------------
// The browser Meta Pixel (installed in index.html) is easily blocked by ad
// blockers and capped by Safari ITP. To still capture those users we also send
// first-party server-side GTM tagging server (services/sgtm, on Cloud Run),
// which forwards events to the Meta Conversions API server-to-server. The
// browser pixel and the server copy share an `event_id` so Meta dedupes them.
//
// Flip this to true ONLY after the sGTM service is deployed and the custom
// domains below are verified — otherwise the first-party gtag loader 404s in
// production. See services/sgtm/README.md.
const ENABLE_TAGGING_SERVER = true;

// First-party endpoints mapped to the sGTM Cloud Run service. Two domains so
// the endpoint is same-site (first-party cookies) on both web origins; the
// client picks the one matching the current page's registrable domain.
const TAGGING_SERVER_URL_APP = "https://ss.uniqueminds.app";
const TAGGING_SERVER_URL_COM = "https://ss.uniqueminds.com";

// Dedicated GA4 stream used purely as the browser->sGTM transport. It is a
// SEPARATE stream from the Firebase Analytics one (G-Y3950DJRSH) on purpose:
// routing this id's hits to the first-party server must not touch Firebase's
// analytics. GA4 itself never receives this data — the sGTM GA4 client claims
// the hits and the Meta CAPI tag fires on the um_* conversions.
const GA4_MEASUREMENT_ID = "G-YPLLLSH30D";

/** Same pixel id as the base snippet in index.html. */
const META_PIXEL_ID = "1358150222094807";

// Our own copies of Meta's click/browser ids. See captureMetaClickIds().
const FBC_STORAGE_KEY = "umMetaFbc";
const FBP_STORAGE_KEY = "umMetaFbp";
/** Raw click context kept alongside the ids, for the User doc + debugging. */
const FBCLID_META_STORAGE_KEY = "umMetaClickMeta";

type Gtag = (...args: unknown[]) => void;

// `window.fbq` is created by the Meta Pixel snippet in index.html (web only);
// this module only calls it to report conversions.
type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

/**
 * First-party sGTM endpoint for the current page. Returns the host on the same
 * registrable domain as the page so its cookies are first-party; `.web.app` and
 * anything else falls back to the `.app` host (third-party, still functional).
 */
function resolveTaggingServerUrl(): string {
  if (typeof window === "undefined") return TAGGING_SERVER_URL_APP;
  const host = window.location.hostname;
  if (host === "uniqueminds.com" || host.endsWith(".uniqueminds.com")) {
    return TAGGING_SERVER_URL_COM;
  }
  return TAGGING_SERVER_URL_APP;
}

// ---------------------------------------------------------------------------
// Match keys (`fbc` / `fbp` / `external_id`)
// ---------------------------------------------------------------------------
// Meta scores every event on Event Match Quality: how confidently it can tie the
// event back to a person, and therefore to the ad click that caused it. Without
// match keys an event carries only IP + user-agent, which scores ~3/10 and is
// close to useless for optimization.
//
// `_fbc` (the click id) and `_fbp` (the browser id) are normally written as
// cookies by fbevents.js. Relying on that alone is circular: when the pixel is
// blocked — the exact case the Conversions API exists to cover — no cookie is
// written, so the server copy has nothing either. ITP also caps JS-set cookies
// at 7 days, so long-lived sessions silently lose them.
//
// So we capture `fbclid` from the landing URL ourselves and keep our own copy of
// both ids, independent of the pixel. Real cookies still win when present (the
// pixel is authoritative); ours are the fallback. Values are sent explicitly on
// every server-side event rather than left for the tag to rediscover.

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Meta's documented shape for both cookies: `fb.<subdomainIndex>.<createdMs>.<payload>`. */
function buildFbCookieValue(payload: string): string {
  return `fb.1.${Date.now()}.${payload}`;
}

/**
 * Capture `fbclid` off the current URL into our own `_fbc` copy, and mint an
 * `_fbp` if neither the pixel nor a previous visit has one. Safe to call more
 * than once; an existing click id is only replaced by a newer click.
 */
export function captureMetaClickIds() {
  if (typeof window === "undefined") return;
  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) {
      // A fresh click always wins — this visit is what we want to attribute.
      localStorage.setItem(FBC_STORAGE_KEY, buildFbCookieValue(fbclid));
      localStorage.setItem(
        FBCLID_META_STORAGE_KEY,
        JSON.stringify({
          fbclid,
          capturedAt: new Date().toISOString(),
          landingUrl: `${window.location.pathname}${window.location.search}`,
        })
      );
      console.log("[social] captured fbclid for Meta matching");
    } else {
      // No fresh click on this URL: snapshot the pixel's own cookie while it
      // still exists. ITP expires it after 7 days, and without a copy getFbc()
      // would have nothing left to fall back on for a later conversion.
      const cookieFbc = readCookie("_fbc");
      if (cookieFbc) localStorage.setItem(FBC_STORAGE_KEY, cookieFbc);
    }
    if (!readCookie("_fbp") && !localStorage.getItem(FBP_STORAGE_KEY)) {
      const random = `${Math.floor(Math.random() * 1e10)}`;
      localStorage.setItem(FBP_STORAGE_KEY, buildFbCookieValue(random));
    }
  } catch (error) {
    // Private-mode localStorage can throw; matching degrades, nothing breaks.
    console.warn("[social] could not capture Meta click ids", error);
  }
}

function readStored(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Pixel-written cookie first, our captured copy as the fallback. */
function getFbc(): string | undefined {
  return readCookie("_fbc") ?? readStored(FBC_STORAGE_KEY);
}

function getFbp(): string | undefined {
  return readCookie("_fbp") ?? readStored(FBP_STORAGE_KEY);
}

/**
 * Stable per-person id. `logUser` only sets the Firebase Analytics user id on
 * web and never reaches this module, so read the signed-in uid directly rather
 * than plumbing another callback through appConfig. Anonymous sessions have a
 * uid too, which is what ties a web visitor to the account they later claim.
 */
function getExternalId(): string | undefined {
  try {
    return auth.currentUser?.uid ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The strongest match key we have, and the only one that survives a change of
 * browser. OAuth sign-in bounces the user out of Meta's in-app browser into the
 * system browser, where no `_fbc` cookie was ever written — so um_sign_in
 * reaches Meta with nothing tying it back to the click. Email crosses that gap.
 *
 * Sent raw to our own first-party tagging server; the sGTM CAPI tag normalizes
 * and SHA-256 hashes it before anything leaves for Meta.
 */
function getEmail(): string | undefined {
  try {
    return auth.currentUser?.email ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Pin the captured click ids to the User document.
 *
 * Cookies and localStorage are per-browser and short-lived; the ad click has to
 * outlive both to be usable from the native app or from a purchase weeks later.
 * The uid survives the web→app handoff (web sign-in upgrades the anonymous user
 * in place), so the User doc is the durable home for these.
 *
 * Idempotent per (uid, fbc) — this is called on every conversion event, but only
 * writes when the pair actually changes.
 */
let persistedAttributionKey: string | undefined;

async function persistMetaAttribution(uid: string | undefined) {
  if (!uid) return;
  const fbc = getFbc();
  const fbp = getFbp();
  if (!fbc && !fbp) return;

  const key = `${uid}:${fbc ?? ""}:${fbp ?? ""}`;
  if (key === persistedAttributionKey) return;
  persistedAttributionKey = key;

  let clickMeta: { fbclid?: string; capturedAt?: string; landingUrl?: string } =
    {};
  try {
    const raw = readStored(FBCLID_META_STORAGE_KEY);
    if (raw) clickMeta = JSON.parse(raw);
  } catch {
    // Corrupt cache is not worth failing a conversion over.
  }

  // localStorage can only hold the ISO string; convert to a Timestamp here so
  // the stored value is the real capture moment, not the time of this write.
  const capturedAt = clickMeta.capturedAt
    ? new Date(clickMeta.capturedAt)
    : undefined;
  const capturedAtValid =
    capturedAt && !Number.isNaN(capturedAt.getTime()) ? capturedAt : undefined;

  const metaAttribution: MetaAttribution = {
    ...(fbc ? { fbc } : {}),
    ...(fbp ? { fbp } : {}),
    ...(clickMeta.fbclid ? { fbclid: clickMeta.fbclid } : {}),
    ...(capturedAtValid
      ? { capturedAt: Timestamp.fromDate(capturedAtValid) }
      : {}),
    ...(clickMeta.landingUrl ? { landingUrl: clickMeta.landingUrl } : {}),
  };

  try {
    await setDoc(
      getDocument<User>(`User/${uid}`),
      { metaAttribution },
      { merge: true }
    );
    console.log("[social] persisted Meta attribution to User", uid);
  } catch (error) {
    // Never let attribution bookkeeping break a conversion event.
    persistedAttributionKey = undefined; // allow a retry on the next event
    logException(error);
    console.error("[social] failed to persist Meta attribution", error);
  }
}

/** Advanced matching is set once per identity, not per event. */
let advancedMatchingId: string | undefined;

function applyPixelAdvancedMatching(externalId: string | undefined) {
  if (!externalId || externalId === advancedMatchingId) return;
  if (typeof window === "undefined" || !window.fbq) return;
  // Re-calling init with advanced matching data updates it; fbevents hashes
  // external_id itself, so the raw uid is correct here.
  window.fbq("init", META_PIXEL_ID, { external_id: externalId });
  advancedMatchingId = externalId;
  console.log("[social] Meta Pixel advanced matching set");
}

/** Unique id shared by the browser pixel and the CAPI copy so Meta dedupes. */
function newEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `e-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

/**
 * Boots gtag pointed at the first-party sGTM tagging server (web only). The
 * loader itself is fetched from the first-party domain so even the script
 * request avoids known tracking domains. Idempotent. No-op until the sGTM
 * service is live (ENABLE_TAGGING_SERVER).
 */
function initTaggingServer() {
  if (!ENABLE_TAGGING_SERVER) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const serverUrl = resolveTaggingServerUrl();

  // Reuse the gtag/dataLayer that Firebase Analytics already installs (it owns a
  // different measurement id). Only bootstrap them ourselves if gtag isn't
  // present — bailing when it exists would skip our config entirely, which is
  // why no hits were reaching the server before.
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    const gtag: Gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag = gtag;
    window.gtag("js", new Date());
  }

  // Register our dedicated transport id and route its collection hits through
  // the first-party tagging server. Additive: it does not change the Firebase
  // stream's destination.
  window.gtag("config", GA4_MEASUREMENT_ID, {
    server_container_url: serverUrl,
    transport_url: serverUrl,
  });

  // Load this id's tag config from the first-party server too.
  const s = document.createElement("script");
  s.async = true;
  s.src = `${serverUrl}/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  console.log("[social] sGTM transport initialized", serverUrl);
}

/**
 * Reports a conversion event to the Meta Pixel (web only). Custom events keep
 * the same `um_*` names used for AppsFlyer so both platforms emit matching data.
 *
 * Fires two copies sharing one `event_id`: the browser pixel (fast, but
 * blockable) and — when the sGTM transport is enabled — a server-side copy that
 * reaches Meta via the Conversions API even when the browser pixel is blocked.
 * Meta dedupes the pair on `event_id`.
 */
function trackMetaPixelEvent(event: SocialEvent) {
  const eventId = newEventId();

  // Re-check the URL every time: on the web funnel the landing hit and the
  // first conversion can straddle a client-side navigation, and the CTA that
  // brought the user here may be the only place fbclid ever appeared.
  captureMetaClickIds();

  const externalId = getExternalId();
  const email = getEmail();
  const fbc = getFbc();
  const fbp = getFbp();

  // Fire-and-forget: pin the click ids to the user so app-side and later
  // server-side events can still reach back to this ad.
  void persistMetaAttribution(externalId);

  if (typeof window !== "undefined" && window.fbq) {
    applyPixelAdvancedMatching(externalId);
    window.fbq("trackCustom", event, {}, { eventID: eventId });
    console.log(`[social] Meta Pixel trackCustom "${event}" (${eventId})`);
  } else {
    console.warn(`[social] Meta Pixel not initialized - cant track "${event}"`);
  }

  if (ENABLE_TAGGING_SERVER && typeof window !== "undefined" && window.gtag) {
    // Match keys go on the server copy explicitly. The tagging server can read
    // first-party cookies itself, but only when they exist — passing them means
    // a blocked pixel (no cookie written) still produces a matchable event.
    // `user_data.email_address` is the cross-browser key: fbc/fbp only help
    // when the conversion fires in the same browser that took the ad click.
    window.gtag("event", event, {
      send_to: GA4_MEASUREMENT_ID,
      event_id: eventId,
      event_source_url: window.location.href,
      ...(fbc ? { fbc } : {}),
      ...(fbp ? { fbp } : {}),
      ...(externalId ? { external_id: externalId } : {}),
      ...(email ? { user_data: { email_address: email } } : {}),
    });
    const keys = [
      fbc && "fbc",
      fbp && "fbp",
      externalId && "external_id",
      email && "email",
    ].filter(Boolean);
    console.log(
      `[social] sGTM event "${event}" (${eventId}) match keys: ${
        keys.length ? keys.join(", ") : "none"
      }`
    );
  }
}

export async function enableSocialEvents() {
  try {
    // If desired we can request IDFA tracking permission from the user
    if (
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === "ios" &&
      ENABLE_IDFA_TRACKING
    ) {
      let statusResponse = await AppTrackingTransparency.getStatus();
      console.log(
        `[social] AppTrackingTransparency status is ${statusResponse.status}`
      );
      if (statusResponse.status === "notDetermined") {
        console.log("[social] AppTrackingTransparency requesting permission");
        const requestPerms = await AppTrackingTransparency.requestPermission();
        console.log(
          `[social] AppTrackingTransparency status is ${requestPerms.status}`
        );
      }
    }

    // Appsflyer initialization
    if (Capacitor.isNativePlatform()) {
      console.log("[social] Initializing AppsFlyer");
      // see https://github.com/AppsFlyerSDK/appsflyer-capacitor-plugin/blob/main/docs/BasicIntegration.md
      const { res } = await AppsFlyer.initSDK({
        // iOS
        appID: "6743822738",
        devKey: "cVvWn7d8C386RMKLRRK9ND",
        isDebug: false, // set to true for debugging
        waitForATTUserAuthorization: ENABLE_IDFA_TRACKING ? 10 : undefined, // for iOS 14 and higher
        minTimeBetweenSessions: 6, // default 5 sec
        registerOnDeepLink: true,
        registerConversionListener: true,
        registerOnAppOpenAttribution: false,
        deepLinkTimeout: 4000, // default 3000 ms
        //useReceiptValidationSandbox: true, // iOS only
        //useUninstallSandbox: true, // iOS only
      });

      // Shared handler for AppsFlyer events
      const handleAppsFlyerEvent = async ({
        eventType,
        eventData,
        firestoreField,
      }: {
        eventType: string;
        eventData: any;
        errorMessage?: string;
        firestoreField: string;
      }) => {
        console.log(`[social] AppsFlyer ${eventType} event`, eventData);
        // Conversion attribution can be replayed on later launches. Only a
        // fresh deep link or first-install conversion expresses navigation intent.
        if (
          eventData?.deep_link_value &&
          (firestoreField === "deepLinkData" ||
            eventData.is_first_launch === true ||
            eventData.is_first_launch === "true")
        ) {
          captureWebHandoffLink(eventData.deep_link_value);
        }
        try {
          if (eventData) {
            // store in local storage in the case when the user is not logged in
            localStorage.setItem(firestoreField, JSON.stringify(eventData));
            if (auth.currentUser) {
              // save the data to the user document
              await setDoc(
                getDocument<User>(`User/${auth.currentUser.uid}`),
                {
                  [firestoreField]: eventData,
                },
                { merge: true }
              );
            } else {
              console.warn(
                `[social] No user is logged in - cant set ${eventType} data`,
                eventData
              );
            }
          } else {
            console.warn(
              `[social] No data is availible in the ${eventType} event`,
              eventData
            );
          }
        } catch (error) {
          logException(error);
          console.error(
            `[social] Error processing AppsFlyer ${eventType} event`,
            error
          );
        }
      };

      // AppsFlyer conversion event
      AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (event) =>
        handleAppsFlyerEvent({
          eventType: event.callbackName,
          eventData: event.data,
          errorMessage: event.errorMessage,
          firestoreField: "conversionData",
        })
      );

      // AppsFlyer attribution event
      AppsFlyer.addListener(AFConstants.OAOA_CALLBACK, (event) =>
        handleAppsFlyerEvent({
          eventType: event.callbackName,
          eventData: event.data,
          errorMessage: event.errorMessage,
          firestoreField: "attributionData",
        })
      );

      // AppsFlyer deep link event
      AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (event) =>
        handleAppsFlyerEvent({
          eventType: "deep link",
          eventData: event.deepLink,
          errorMessage: event.error,
          firestoreField: "deepLinkData",
        })
      );

      if (config.isNativePlatform && config.nativeAnalytics?.getAppInstanceId) {
        const appInstanceId = await config.nativeAnalytics.getAppInstanceId();
        if (appInstanceId) {
          console.log(
            "[social] Setting appInstanceId to AppsFlyer",
            appInstanceId
          );
          await AppsFlyer.setAdditionalData({
            additionalData: {
              app_instance_id: appInstanceId,
            },
          });
        } else {
          console.warn("[social] No appInstanceId is availible");
        }
      }
      console.log("[social] AppsFlyer initialized", res);
    } else {
      // Web-hosted version: the Meta Pixel base code (init + PageView) is
      // loaded inline in index.html. Here we only wire up the first-party sGTM
      // transport so conversions also reach Meta via the Conversions API.
      console.log("[social] Initializing web analytics (sGTM transport)");
      // Capture the click id from the landing URL before any client-side
      // navigation drops it — this is the only moment fbclid is guaranteed
      // to be present, and every later conversion depends on having it.
      captureMetaClickIds();
      initTaggingServer();
    }
  } catch (error) {
    logException(error);
    console.error(`[social] Error enabling social events`, error);
  }
}

export { getAttributionData, setUserId } from "./socialAttribution";

export type SocialEvent =
  | "app_download_clicked"
  | "um_sign_in"
  | "um_create_character"
  | "um_enable_notifications"
  | "um_accept_daily_loop"
  | "um_accept_daily_boosts"
  | "um_accept_daily_digest"
  | "um_daily_loop_completed"
  | "um_routine_selected"
  | "um_wildcard_routed"
  | "um_paywall_shown"
  | "um_paywall_dismissed"
  | "um_start_trial"
  | "um_subscribe"
  | "support_setup_started"
  | "support_intent_parsed"
  | "support_draft_shown"
  | "support_guess_edited"
  | "support_card_confirmed"
  | "support_abandoned"
  | "support_queue_completed";

/**
 * AppsFlyer predefined event names get the standard partner mapping (e.g. Meta's
 * StartTrial / Subscribe) instead of arriving as generic custom events, so ad
 * networks can optimize on them. Firebase keeps the um_* names.
 */
const APPSFLYER_EVENT_NAME_OVERRIDES: Partial<Record<SocialEvent, string>> = {
  um_start_trial: "af_start_trial",
  um_subscribe: "af_subscribe",
};

export async function sendConversionEvent(
  event: SocialEvent,
  params?: Record<string, string | number>
) {
  console.log(
    `[social] 🚀 sendConversionEvent called with: "${event}", isNative: ${Capacitor.isNativePlatform()}`,
    params || ""
  );
  try {
    if (Capacitor.isNativePlatform()) {
      const eventName = APPSFLYER_EVENT_NAME_OVERRIDES[event] ?? event;
      const { res } = await AppsFlyer.logEvent({
        eventName,
        ...(params ? { eventValue: params } : {}),
      });
      console.log(`[social] AppsFlyer logEvent ${eventName}`, res);
    } else {
      // Web-hosted version reports the same event through the Meta Pixel.
      trackMetaPixelEvent(event);
    }
    logEvent(event, params);
    console.log(`[social] ✅ logEvent("${event}") dispatched`);
  } catch (error) {
    logException(error);
    console.error(`[social] Error sending conversion event: ${event}`, error);
  }
}
